/**
 * SSE-aware client for the new Swiss Parliament chatbot backend.
 *
 * Talks to `POST /db/chat/messages` (FastAPI) and consumes a Server-Sent
 * Events stream. Each event maps to one of our agreed SSE event names; the
 * caller plugs in optional handlers for the ones it cares about.
 *
 * Sessions are auto-created server-side: pass `sessionId: null` (or omit)
 * on the first turn, and the `done` event will return the new UUID.
 *
 * `innochat.ts` is intentionally left untouched so we can roll back via
 * `git revert` if the new path needs to be disabled in production.
 */

import {
  CitationsPayloadSchema,
  ChatErrorInfoSchema,
  DebugInfoSchema,
  DoneInfoSchema,
  FilterSuggestionsPayloadSchema,
  FollowUpsPayloadSchema,
  LinkablePeoplePayloadSchema,
  TextDeltaPayloadSchema,
  ToolCallInfoSchema,
  validateChatEvent,
} from './innoqSchemas';

const DB_API_BASE = (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:8000';
const CHAT_ENDPOINT = `${DB_API_BASE}/db/chat/messages`;

// ── Public types ──────────────────────────────────────────────────────────────

export interface DashboardState {
  /** Current dashboard page, e.g. 'members', 'bills', 'votes'. */
  page: string;
  /**
   * Entity being viewed on a detail page (e.g. /members/4103, /bills/25.081),
   * so the model can resolve "this bill" / "he" / "them" to it. Null on list
   * and overview pages. `label` is the on-screen name (councillor name, bill
   * title) — for people it's the actionable key (tools search by name).
   */
  entity?: { type: string; id: string; label?: string; affair?: string | null } | null;
  /** Active filter state for that page. Free-form to match the model's expectations. */
  filters: Record<string, unknown>;
}

export interface FiltersDirective {
  page: string;
  filters: Record<string, unknown>;
}

/**
 * Clickable filter-suggestion pill emitted by the chat agent.
 * Replaces v1's auto-applied `FiltersDirective`. The user clicks → frontend
 * navigates. `type: 'entity'` carries an explicit `url`; `type: 'list'`
 * (default) builds a URL from `page` + `filters` via chatDirectives.
 */
export interface FilterSuggestion {
  /** Imperative phrase shown on the pill ("Browse 12 SVP NR members on UREK"). */
  label: string;
  /** Target dashboard page name (matches `chatDirectives.PAGE_TO_PATH`). */
  page: string;
  /** Filter key-value pairs — keys must match per-page URL params. */
  filters?: Record<string, unknown>;
  /** Visual + behavioural variant. Defaults to 'list'. */
  type?: 'list' | 'entity' | 'stats';
  /** Required when `type: 'entity'` — direct detail-page URL. */
  url?: string;
}

export interface Citation {
  /** Matches the [N] marker in the assistant's prose. */
  id: number;
  /** Human-readable label shown on hover (entity name, affair number + title, etc.). */
  label: string;
  /** Canonical URL — opened in a new tab on click. */
  url: string;
  /** Entity kind — drives the citation pill's icon/colour. */
  type?: 'councillor' | 'affair' | 'dispatch' | 'speech';
  /** For affair/dispatch citations: the bare bill number (e.g. "25.026").
   *  Rendered as a rounded bill-number button instead of the numeric pill. */
  ref?: string;
  /** For councillor citations only: portrait image URL (parlament.ch), or
   *  null/absent when the councillor has no resolvable portrait. A councillor
   *  citation renders as an avatar (or a person glyph when null) — NEVER a
   *  number. Ignored for speech citations (which keep the numeric pill). */
  portrait?: string | null;
}

/**
 * People-by-name citation safety net. The backend ships EVERY councillor the
 * turn gathered ({name, url}) on a side-channel (never into the model context),
 * so the frontend can attach a profile link wherever the model named a
 * councillor in its answer but forgot the `[N]` marker.
 */
export interface LinkablePerson {
  /** Full display name (first + last) to match against the answer text. */
  name: string;
  /** Councillor profile URL, opened on click. */
  url: string;
  /** Portrait image URL (parlament.ch), or null/absent when the councillor has
   *  no portrait — the pill then renders the name link with no avatar. */
  portrait?: string | null;
}

export interface ToolCallInfo {
  name: string;
  id: string;
}

export interface DebugToolCall {
  id: string;
  name: string;
  args: Record<string, unknown>;
  result_preview: string;
}

export interface DebugInfo {
  iteration: number;
  model_duration_ms: number;
  tool_duration_ms: number;
  tool_calls: DebugToolCall[];
}

export interface DoneInfo {
  session_id: string;
  iterations_capped?: boolean;
}

export interface ChatErrorInfo {
  message: string;
  code: string;
}

export interface StreamChatOptions {
  /** Async accessor for the Clerk session JWT (Audit C1) — typically
   *  `useAuth().getAccessToken`. Backend reads the verified `sub` claim
   *  from the Authorization header; user_id no longer goes in the body. */
  getToken: () => Promise<string | null | undefined>;
  query: string;
  sessionId?: string | null;
  dashboardState?: DashboardState | null;
  /** Developer-only per-request provider override (widget Speed/Quality pill).
   *  'speed' → fast model, 'quality' → best model. The backend honours it only
   *  for users with the developer role. */
  mode?: 'speed' | 'quality' | null;

  onTextDelta?: (chunk: string) => void;
  onToolCall?: (tool: ToolCallInfo) => void;
  onFilterSuggestions?: (items: FilterSuggestion[]) => void;
  onFollowUps?: (items: string[]) => void;
  onCitations?: (items: Citation[]) => void;
  onLinkablePeople?: (items: LinkablePerson[]) => void;
  onDebug?: (info: DebugInfo) => void;
  onError?: (err: ChatErrorInfo) => void;

  /** AbortSignal to cancel mid-stream. */
  signal?: AbortSignal;
}

export interface StreamChatResult {
  /** Full assembled text (concatenation of all text-delta chunks). */
  fullText: string;
  /** Server-assigned session UUID — store this for follow-up turns. */
  sessionId: string;
  /** Filter-suggestion pills the user can click to navigate to filtered views. */
  filterSuggestions: FilterSuggestion[];
  /** Suggested follow-up questions in the user's query language. */
  followUps: string[];
  /** Inline citations referenced by [N] markers in the answer text. */
  citations: Citation[];
  /** People-by-name safety net — councillors gathered this turn, name-matched
   *  in the answer text for profile links the model didn't explicitly cite. */
  linkablePeople: LinkablePerson[];
  /** Per-iteration debug info — only populated for users with the dev role. */
  debug: DebugInfo[];
  /** True if the agent loop hit MAX_ITERATIONS. */
  iterationsCapped: boolean;
  /** True if an error event was received mid-stream. */
  errored: boolean;
  /** Id of the persisted assistant message — used as the target for the
   *  feedback endpoint. Undefined on cap-warning / edge-case `done`
   *  emissions; the frontend hides the feedback UI in that case. */
  messageId?: string;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Send a query to the chatbot backend and consume the SSE stream.
 *
 * Returns once the stream completes (`done` or `error` event, or the
 * connection closes). Handlers are invoked synchronously for each event.
 */
export async function streamChat(opts: StreamChatOptions): Promise<StreamChatResult> {
  const result: StreamChatResult = {
    fullText: '',
    sessionId: opts.sessionId ?? '',
    filterSuggestions: [],
    followUps: [],
    citations: [],
    linkablePeople: [],
    debug: [],
    iterationsCapped: false,
    errored: false,
  };

  const body: Record<string, unknown> = {
    query: opts.query,
  };
  if (opts.sessionId) body.session_id = opts.sessionId;
  if (opts.dashboardState) body.dashboard_state = opts.dashboardState;
  if (opts.mode) body.mode = opts.mode;

  // Audit C1 — attach the Clerk JWT so the backend can verify `sub`.
  // Fetched fresh per call so token rotation is transparent.
  let token: string | null | undefined;
  try {
    token = await opts.getToken();
  } catch {
    token = null;
  }
  if (!token) {
    const errInfo: ChatErrorInfo = { message: 'Not signed in', code: 'no_auth' };
    result.errored = true;
    opts.onError?.(errInfo);
    return result;
  }

  const response = await fetch(CHAT_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
    signal: opts.signal,
  });

  if (!response.ok) {
    const text = await response.text();
    const errInfo: ChatErrorInfo = {
      message: `HTTP ${response.status}: ${text || response.statusText}`,
      code: 'http_error',
    };
    result.errored = true;
    opts.onError?.(errInfo);
    return result;
  }

  if (!response.body) {
    const errInfo: ChatErrorInfo = { message: 'Response has no body', code: 'no_body' };
    result.errored = true;
    opts.onError?.(errInfo);
    return result;
  }

  await consumeSSE(response.body, (event, data) => {
    handleEvent(event, data, opts, result);
  });

  return result;
}

// ── SSE consumer ──────────────────────────────────────────────────────────────

/**
 * Read an SSE-formatted ReadableStream and invoke `onEvent` for each complete
 * frame. Frames are separated by a blank line (`\n\n`); each frame may have
 * one `event:` line and one or more `data:` lines.
 */
async function consumeSSE(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: string, data: string) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // Drain complete frames from the buffer.
      let separatorIdx = findFrameSeparator(buffer);
      while (separatorIdx !== -1) {
        const frame = buffer.slice(0, separatorIdx);
        // Skip past the separator itself (\n\n or \r\n\r\n).
        buffer = buffer.slice(separatorIdx + (buffer.startsWith('\r\n', separatorIdx) ? 4 : 2));
        emitFrame(frame, onEvent);
        separatorIdx = findFrameSeparator(buffer);
      }
    }

    // Drain any final non-terminated frame on stream close.
    if (buffer.trim().length > 0) {
      emitFrame(buffer, onEvent);
    }
  } finally {
    reader.releaseLock();
  }
}

function findFrameSeparator(buf: string): number {
  // Prefer \n\n; fall back to \r\n\r\n if present.
  const n = buf.indexOf('\n\n');
  const r = buf.indexOf('\r\n\r\n');
  if (n === -1) return r;
  if (r === -1) return n;
  return Math.min(n, r);
}

function emitFrame(frame: string, onEvent: (event: string, data: string) => void): void {
  let event = '';
  const dataLines: string[] = [];

  for (const rawLine of frame.split('\n')) {
    const line = rawLine.replace(/\r$/, '');
    if (line.startsWith('event:'))      event = line.slice('event:'.length).trim();
    else if (line.startsWith('data:'))  dataLines.push(line.slice('data:'.length).replace(/^ /, ''));
    // id:, retry:, and comment lines (starting with ':') are intentionally ignored.
  }

  if (event && dataLines.length > 0) {
    onEvent(event, dataLines.join('\n'));
  }
}

// ── Feedback API ──────────────────────────────────────────────────────────────

export type FeedbackCategory =
  | 'inaccurate'
  | 'incomplete'
  | 'wrong_citation'
  | 'off_topic'
  | 'formatting'
  | 'other';

export interface FeedbackSubmission {
  rating:   'up' | 'down';
  category?: FeedbackCategory;
  comment?:  string;
}

/**
 * Persist user feedback for one assistant message. Idempotent — re-submitting
 * overwrites the prior rating/category/comment. Returns true on success;
 * caller can decide whether to surface the failure (default: silent retry).
 */
export async function submitFeedback(
  messageId: string,
  getToken: () => Promise<string | null | undefined>,
  feedback: FeedbackSubmission,
): Promise<boolean> {
  const token = await getToken().catch(() => null);
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${DB_API_BASE}/db/chat/messages/${encodeURIComponent(messageId)}/feedback`, {
    method:  'POST',
    headers,
    body:    JSON.stringify(feedback),
  });
  return res.ok;
}


// ── Event dispatch ────────────────────────────────────────────────────────────

function handleEvent(
  event: string,
  data: string,
  opts: StreamChatOptions,
  result: StreamChatResult,
): void {
  let payload: unknown;
  try {
    payload = JSON.parse(data);
  } catch {
    // Non-JSON payload — ignore. (Defensive; backend always emits JSON.)
    return;
  }

  switch (event) {
    case 'text-delta': {
      const parsed = validateChatEvent(event, payload, TextDeltaPayloadSchema);
      if (!parsed) return;
      const { delta } = parsed;
      if (delta) {
        result.fullText += delta;
        opts.onTextDelta?.(delta);
      }
      return;
    }

    case 'tool-call': {
      const info = validateChatEvent(event, payload, ToolCallInfoSchema);
      if (!info) return;
      opts.onToolCall?.(info);
      return;
    }

    case 'filter-suggestions': {
      const parsed = validateChatEvent(event, payload, FilterSuggestionsPayloadSchema);
      if (!parsed) return;
      const items = parsed.items as FilterSuggestion[];
      result.filterSuggestions = items;
      opts.onFilterSuggestions?.(items);
      return;
    }

    case 'follow-ups': {
      const parsed = validateChatEvent(event, payload, FollowUpsPayloadSchema);
      if (!parsed) return;
      result.followUps = parsed.items;
      opts.onFollowUps?.(parsed.items);
      return;
    }

    case 'citations': {
      const parsed = validateChatEvent(event, payload, CitationsPayloadSchema);
      if (!parsed) return;
      const items = parsed.items as Citation[];
      result.citations = items;
      opts.onCitations?.(items);
      return;
    }

    case 'linkable-people': {
      const parsed = validateChatEvent(event, payload, LinkablePeoplePayloadSchema);
      if (!parsed) return;
      const items = parsed.items as LinkablePerson[];
      result.linkablePeople = items;
      opts.onLinkablePeople?.(items);
      return;
    }

    case 'debug': {
      const info = validateChatEvent(event, payload, DebugInfoSchema);
      if (!info) return;
      result.debug.push(info as DebugInfo);
      opts.onDebug?.(info as DebugInfo);
      return;
    }

    case 'done': {
      const info = validateChatEvent(event, payload, DoneInfoSchema);
      if (!info) return;
      if (info.session_id) result.sessionId = info.session_id;
      if (info.iterations_capped) result.iterationsCapped = true;
      if (info.message_id) result.messageId = info.message_id;
      return;
    }

    case 'error': {
      const err = validateChatEvent(event, payload, ChatErrorInfoSchema);
      if (!err) {
        // Even malformed errors should flag the result as errored so the
        // caller doesn't silently treat the stream as successful.
        result.errored = true;
        return;
      }
      result.errored = true;
      opts.onError?.(err);
      return;
    }

    default:
      // Unknown event — ignore. Forward-compat for future event types.
      return;
  }
}
